import { Fragment, useEffect, useMemo, useState } from 'react'

import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { PkgWarehouse } from '@/api/user-package/types/UserPkg'
import { Chip } from '@/components/Chip'
import { Divider } from '@/components/Divider'
import EuropeIcon from '@/components/icons/europe.svg?react'
import InfoIcon from '@/components/icons/info.svg?react'
import { SegmentedButton } from '@/components/SegmentedButton'
import { Card, CardContent } from '@/components/ui/card'
import { WarehousePreview } from '@/components/WarehousePreview'

import { PrimarkForm } from './components/PrimarkForm'
import { WarehouseAddressPreview } from './components/WarehouseAddressPreview'
import HeaderIcon from './header-icon.svg?react'

type Group = 'USA' | 'EUR' | 'UK'
type WarehouseOption = 'USA' | 'USA-tf' | PkgWarehouse.GR | PkgWarehouse.UK | 'primark-click-collect'
const usaNote = (
  <p className='text-text-secondary flex-1 text-right text-xs leading-4'>
    מומלץ לשלוח לדלאוור הזמנות מעל $100 שאינן כוללות מוצרי אופנה. משלוח לדלאוור הינו בתוספת תשלום בהתאם{' '}
    <a href='https://shiptanbul.com/calculator/costs/usa' target='_blank' rel='noopener noreferrer' className='text-text-link underline'>
      למחירון
    </a>
  </p>
)
const primarkNote = (
  <p className='text-text-secondary flex-1 text-right text-xs leading-4'>
    כדי שנוכל לטפל בהזמנה שלך, יש להזין את מספר ההזמנה ולהעלות את הברקוד שקיבלת. אם עדיין אין לך את כל הפרטים, חזור לכאן מאוחר יותר והשלם את
    המידע{' '}
    <a href='#' target='_blank' rel='noopener noreferrer' className='text-text-link underline'>
      לפרטים נוספים על השירות
    </a>
  </p>
)
const groups = [
  {
    value: 'USA',
    label: 'ארצות הברית',
    warehouses: [PkgWarehouse.USA, 'USA-tf'],
    flag: <WarehousePreview warehouse={PkgWarehouse.USA} className='size-[18px]' />,
  },
  {
    label: 'אירופה',
    value: 'EUR',
    warehouses: [PkgWarehouse.GR],
    flag: <EuropeIcon />,
  },
  {
    value: 'UK',
    label: 'אנגליה',
    warehouses: [PkgWarehouse.UK],
    flag: <WarehousePreview warehouse={PkgWarehouse.UK} className='size-[18px]' />,
  },
]
const warehouseOptions: { group: string; value: string; label: string; note?: React.ReactNode; flag: React.ReactNode }[] = [
  {
    group: 'USA',
    value: PkgWarehouse.USA,
    label: 'ניו ג׳רזי',
    note: usaNote,
    flag: <WarehousePreview warehouse={PkgWarehouse.USA} className='size-[18px]' />,
  },
  {
    group: 'USA',
    value: 'USA-tf',
    label: 'דלאוור',
    note: usaNote,
    flag: <WarehousePreview warehouse={PkgWarehouse.USA} className='size-[18px]' />,
  },
  {
    group: 'UK',
    value: PkgWarehouse.UK,
    label: 'אנגליה',
    flag: <WarehousePreview warehouse={PkgWarehouse.UK} className='size-[18px]' />,
  },
  {
    group: 'EUR',
    value: PkgWarehouse.GR,
    label: 'יוון',
    flag: <WarehousePreview warehouse={PkgWarehouse.GR} className='size-[18px]' />,
    note: 'לכתובת זו ניתן להזמין מכל רחבי אירופה',
  },
]
interface AddressField {
  label: string
  value: string
  highlight?: boolean
  note?: string
}

interface AddressData {
  country: Group
  title: string
  warehouse: PkgWarehouse
  fields: AddressField[]
}

const getAddressData = (option: WarehouseOption, phoneNumber: string, fullName: string): AddressData | null => {
  const phoneSuffix = phoneNumber?.split('+9720')[1] || phoneNumber?.split('+972')[1] || ''
  if (option === 'USA') {
    return {
      country: 'USA',
      title: 'הכתובת האמריקאית שלך',
      warehouse: PkgWarehouse.USA,
      fields: [
        { label: 'Full Name', value: fullName || 'Full Name' },
        { label: 'Address Line 1', value: '600 Markley St' },
        { label: 'Address Line 2', value: `Shiptanbul ${phoneSuffix}` },
        { label: 'Phone', value: '732-634-1517' },
        { label: 'Country', value: 'United States' },
        { label: 'State', value: 'New Jersey (NJ)' },
        { label: 'City', value: 'Port Reading' },
        { label: 'ZIP Code', value: '07064' },
      ],
    }
  }
  if (option === 'USA-tf') {
    return {
      country: 'USA',
      title: 'הכתובת האמריקאית שלך',
      warehouse: PkgWarehouse.USA,
      fields: [
        { label: 'Full Name', value: fullName || 'Full Name' },
        { label: 'Address Line 1', value: '3601 Old Capitol Trail' },
        { label: 'Address Line 2', value: `Unit A-7, Shiptanbul ${phoneSuffix}` },
        { label: 'Phone', value: '732-634-1517' },
        { label: 'Country', value: 'United States' },
        { label: 'State', value: 'Delaware' },
        { label: 'City', value: 'Wilmington' },
        { label: 'ZIP Code', value: '19808' },
      ],
    }
  }

  if (option === PkgWarehouse.GR) {
    return {
      country: 'EUR',
      title: 'הכתובת היוונית שלך',
      warehouse: PkgWarehouse.GR,
      fields: [
        { label: 'Full Name', value: fullName || 'Full Name' },
        { label: 'Address Line 1 (διεύθυνση)', value: 'THESSALONIKIS 4A' },
        { label: 'Address Line 2 (διεύθυνση) FLAT/APT NUMBER', value: `TROY ${phoneSuffix}` },
        { label: 'Country (χώρα)', value: 'Greece' },
        { label: 'Province (επαρχία)', value: 'ATTIKI' },
        { label: 'City (πόλη)', value: 'NIKAIA-AGIOS\nIOANNIS RENTIS' },
        { label: 'ZIP Code (Ταχυδρομικός Κώδικας)', value: '182 33' },
        { label: 'Phone (τηλέφωνο)', value: '(+30) 6942695403' },
      ],
    }
  }

  if (option === PkgWarehouse.UK) {
    return {
      country: 'UK',
      title: 'הכתובת האנגלית שלך',
      warehouse: PkgWarehouse.UK,
      fields: [
        { label: 'Full Name', value: fullName || 'Full Name' },
        { label: 'Address Line 1', value: 'Unit 5 Maple Grove Business Centre' },
        { label: 'Address Line 2', value: `Shiptanbul ${phoneSuffix}` },
        { label: 'Phone', value: '(+44) 01753684320' },
        { label: 'Country', value: 'United Kingdom' },
        { label: 'State', value: 'Middlesex' },
        { label: 'City', value: 'Hounslow' },
        { label: 'Postcode', value: 'TW4 6DR' },
      ],
    }
  }

  return null
}

export default function MyAddresses() {
  const { data: loggedUser } = useCurrentUser()
  const [selectedGroup, setSelectedGroup] = useState<Group>('USA')
  const [selectedWarehouseOption, setSelectedWarehouseOption] = useState<WarehouseOption>('USA')

  const phoneNumber = loggedUser?.UserAttributes?.phone_number || ''
  const fullName = `${loggedUser?.UserAttributes?.name || ''} ${loggedUser?.UserAttributes?.family_name || ''}`.trim()

  const addressData = getAddressData(selectedWarehouseOption, phoneNumber, fullName)

  const warehouseOptionsToShow = useMemo(() => {
    const result = warehouseOptions.filter(option => option.group === selectedGroup)
    if (selectedGroup === 'UK' && loggedUser?.permissions?.Admin) {
      result.push({
        group: 'UK',
        value: 'primark-click-collect',
        label: 'Primark-Click & Collect',
        flag: <WarehousePreview warehouse={PkgWarehouse.UK} className='size-[18px]' />,
        note: primarkNote,
      })
    }
    return result
  }, [loggedUser?.permissions?.Admin, selectedGroup])

  const noteToShow = useMemo(
    () => warehouseOptionsToShow.find(option => option.value === selectedWarehouseOption)?.note,
    [warehouseOptionsToShow, selectedWarehouseOption]
  )
  useEffect(() => {
    if (warehouseOptionsToShow.length > 0) {
      setSelectedWarehouseOption(warehouseOptionsToShow[0].value as WarehouseOption)
    }
  }, [warehouseOptionsToShow])

  const isPrimarkForm = selectedWarehouseOption === 'primark-click-collect'

  if (!isPrimarkForm && !addressData) return null
  return (
    <div className='mx-auto flex max-w-5xl flex-col gap-4 p-5 md:gap-6 md:p-6' dir='rtl'>
      {/* Header */}
      <div className='flex items-center gap-2'>
        <HeaderIcon />
        <div className='flex flex-col gap-1'>
          <h1 className='text-text-primary text-lg leading-7 font-medium'>כאן תמצא את כל הכתובות שאליהן תוכל להזמין חבילות מכל העולם.</h1>
          <p className='text-text-secondary text-sm leading-[1.5]'>העתק את הכתובת בקליק כדי להשתמש בה בקניות שלך.</p>
        </div>
      </div>

      {/* Filters */}
      <div className='flex flex-col gap-4'>
        <SegmentedButton
          options={groups}
          value={selectedGroup}
          onChange={value => {
            setSelectedGroup(value as Group)
          }}
          className='max-w-[992px]'
        />

        <div className='flex items-center gap-2'>
          {warehouseOptionsToShow.map(option => (
            <Chip
              key={option.value}
              selected={selectedWarehouseOption === option.value}
              onSelect={() => {
                setSelectedWarehouseOption(option.value as WarehouseOption)
              }}
              icon={option.flag}
            >
              {option.label}
            </Chip>
          ))}
        </div>
      </div>

      {/* Delaware Info Note */}
      {noteToShow && (
        <Card className='bg-white p-4 shadow-none'>
          <CardContent className='text-text-secondary flex items-center gap-3 px-0 text-xs'>
            <InfoIcon className='text-text-link' />
            {noteToShow}
          </CardContent>
        </Card>
      )}

      {/* Primark Form or Address Card */}
      {isPrimarkForm ? (
        <PrimarkForm fullName={fullName} />
      ) : (
        <Card className='border-border-default border bg-white shadow-sm'>
          <CardContent>
            <div className='space-y-6'>
              {/* Card Header */}
              <div className='flex items-center gap-2'>
                <WarehousePreview warehouse={addressData!.warehouse} />
                <h2 className='text-text-primary leading-6 font-medium'>{addressData!.title}</h2>
              </div>

              {/* Address Fields */}
              <div className='flex flex-col'>
                {addressData!.fields.map((field, index) => {
                  const isAddressLine2 = field.label.includes('Address Line 2')
                  const isLastField = index === addressData!.fields.length - 1
                  return (
                    <Fragment key={field.label}>
                      <WarehouseAddressPreview index={index} isAddressLine2={isAddressLine2} field={field} />
                      {!isLastField && <Divider className='bg-border-default mt-4 mb-3' />}
                    </Fragment>
                  )
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CTA Button */}
      {/* <div className='flex flex-col items-center gap-2'>
          <Button onClick={() => history.push('/my-area/packages')} size='lg' className='w-[335px]'>
            הזמנתי חבילה חדשה!
          </Button>
          <p className='text-text-secondary text-center text-xs leading-4'>חבילות שלא קיבלו עדכון מתעכבות בזיהוי בעד 2 ימי עסקים</p>
        </div> */}
    </div>
  )
}
